/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { DirectionalHint, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { Intent } from 'src/luis/modules/intents/Intent';

export type UtteranceTableContextualMenuProps = {
    /**
     * Intents provided to the reassign intent action
     */
    intents: Intent[];

    /**
     * Indicates if the menu items is hidden or not.
     */
    isHidden: boolean;

    /**
     * Indicates if the more options button is hidden or not.
     */
    isMoreOptionsButtonHidden?: boolean;

    /**
     * Indicates if the add to patterns option disabled or not.
     */
    isAddAsPatternDisabled: boolean;

    /**
     * Indicates if the confirm entity predictions option disabled or not.
     */
    isConfirmPredictionsDisabled: boolean;

    /**
     * Callback to set the edit mode
     */
    onEdit: (state: boolean) => void;

    /**
     * Callback function for the delete action
     */
    onDelete: () => void;

    /**
     * Callback function for reassign intent action for selected utterances
     */
    onReassignIntent: (intentId: string) => void;

    /**
     * Callback function for add new utterance as pattern action
     */
    onAddToPatterns: () => void;

    /**
     * Callback function for confirm the utterance entity predictions
     */
    onConfirmPrediction: () => void;
};

const messages = defineMessages({
    moreOptions: {
        id: 'utterances.utteranceTableContextualMenu.moreOptions',
        defaultMessage: 'More options'
    },
    confirmEntityPredictions: {
        id: 'utterances.utteranceTableContextualMenu.confirmEntityPredictions',
        defaultMessage: 'Confirm entity predictions'
    },
    edit: {
        id: 'utterances.utteranceTableContextualMenu.edit',
        defaultMessage: 'Edit utterance'
    },
    delete: {
        id: 'utterances.utteranceTableContextualMenu.delete',
        defaultMessage: 'Delete utterance'
    },
    moveTo: {
        id: 'utterances.utteranceTableContextualMenu.moveTo',
        defaultMessage: 'Move to'
    },
    addToPatterns: {
        id: 'utterances.utteranceTableContextualMenu.addToPatterns',
        defaultMessage: 'Add to patterns'
    }
});

const { styleComponent } = getStylistV2('UtteranceTableContextualMenu');
const menuTheme = getScopedTheme('UtteranceTableContextualMenu');

const menuButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { color: menuTheme.iconColor },
    menuIcon: { color: menuTheme.iconColor, fontWeight: 'bold' },
    rootHovered: { color: menuTheme.iconColor },
    rootHasMenu: { color: menuTheme.iconColor },
    rootPressed: { color: menuTheme.iconColor }
});

const OptionsContainer = styleComponent<IStackProps>(Stack)(
    'OptionsContainer',
    (_, cssVars) => ({
        marginTop: 5,
        opacity: cssVars.opacity,
        backgroundColor: 'transparent'
    }),
    {
        cssVars: { opacity: '' },
        cssVarsSetter: ({ isHidden }: { isHidden: boolean }) => ({ opacity: isHidden ? 0 : 1 })
    }
);

/**
 * The contextual menu that appears in the utterance row (on hovering) and manages the
 * add, edit, reassign intent and delete actions for the specified utterance.
 */
export const UtteranceRowOptions = (props: UtteranceTableContextualMenuProps) => {
    const {
        intents,
        onAddToPatterns,
        onDelete,
        onEdit,
        onReassignIntent,
        onConfirmPrediction,
        isConfirmPredictionsDisabled,
        isHidden,
        isAddAsPatternDisabled,
        isMoreOptionsButtonHidden = false
    } = props;
    const { localize } = useLocalization();

    const contextualMenuIntents: IContextualMenuItem[] = intents.map(intent => ({
        key: intent.name,
        text: intent.name,
        onClick: () => onReassignIntent(intent.id as string)
    }));

    const contextualMenuItems: IContextualMenuItem[] = [
        {
            key: 'moveTo',
            text: localize(messages.moveTo),
            iconProps: { iconName: 'MoveToFolder' },
            subMenuProps: { items: contextualMenuIntents }
        },
        {
            key: 'addToPatterns',
            onClick: onAddToPatterns,
            iconProps: { iconName: 'Code' },
            disabled: isAddAsPatternDisabled,
            text: localize(messages.addToPatterns)
        }
    ];

    return (
        <OptionsContainer horizontal customProps={{ isHidden }} verticalAlign="start" data-id="utteranceRowOptions">
            <LuisTooltip content={localize(messages.confirmEntityPredictions)} hidden={isHidden}>
                <IconButton
                    onClick={onConfirmPrediction}
                    iconProps={{ iconName: 'Accept' }}
                    disabled={isConfirmPredictionsDisabled || isHidden}
                    data-automation-id="confirmEntityPredictionsButton"
                    ariaLabel={localize(messages.confirmEntityPredictions)}
                />
            </LuisTooltip>

            <LuisTooltip content={localize(messages.edit)} hidden={isHidden}>
                <IconButton
                    disabled={isHidden}
                    onClick={() => onEdit(true)}
                    iconProps={{ iconName: 'Edit' }}
                    ariaLabel={localize(messages.edit)}
                    data-automation-id="editUtteranceButton"
                />
            </LuisTooltip>

            <LuisTooltip content={localize(messages.delete)} hidden={isHidden}>
                <IconButton
                    disabled={isHidden}
                    onClick={onDelete}
                    iconProps={{ iconName: 'Delete' }}
                    ariaLabel={localize(messages.delete)}
                    data-automation-id="deleteUtteranceButton"
                />
            </LuisTooltip>

            {!isMoreOptionsButtonHidden && (
                <LuisTooltip content={localize(messages.moreOptions)}>
                    <IconButton
                        styles={menuButtonStyles}
                        menuIconProps={{ hidden: true }}
                        iconProps={{ iconName: 'MoreVertical' }}
                        data-automation-id="overflowOptionsButton"
                        ariaLabel={localize(messages.moreOptions)}
                        menuProps={{ directionalHint: DirectionalHint.bottomAutoEdge, items: contextualMenuItems }}
                    />
                </LuisTooltip>
            )}
        </OptionsContainer>
    );
};
